import { useApolloClient } from '@apollo/client/react';
import dayjs from 'dayjs';
import { useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import useActivityTracker from '../../hooks/useActivityTracker';
import extractFromJWT from '../../utils/extractFromJWT';
import { warning as showWarningNotification, remove as removeNotification } from '../../utils/notifications/actions';
import { updateToken, logoutReasonKey } from '../../utils/session';
import { ExtendSessionMutation } from './ExtendSession.graphql';

const WARNING_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // 5 minutes
const REFRESH_BEFORE_EXPIRY_MS = 1 * 60 * 1000; // 1 minute
// small safety margin so the final activity check fires before the token
// actually expires on the server — avoids a race where in-flight queries
// use an expired token and get bounced as UNAUTHENTICATED
const FINAL_CHECK_SAFETY_MARGIN_MS = 3 * 1000;
const INACTIVITY_WARNING_ID = 'session-inactivity-warning';

/**
 * Manages the access-token lifecycle: activity tracking, inactivity warning,
 * conditional refresh, and inactivity logout.
 *
 * Returns a `logout` function the provider can expose in context.
 */
const useSessionLifecycle = (currentToken: string | null) => {
    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation('loginPage');
    const { wasActiveWithin, setOnActivity } = useActivityTracker();

    const logout = useCallback(
        (reason?: 'inactivity') => {
            if (reason === 'inactivity') {
                sessionStorage.setItem(logoutReasonKey, 'inactivity');
            }
            removeNotification(INACTIVITY_WARNING_ID);
            updateToken(null);
            // reset to the default language
            void i18n.changeLanguage('en');
            navigate('/login', { replace: true });
        },
        [navigate, i18n]
    );

    // derive session timing from the JWT so frontend stays in sync with backend TTL
    const tokenTimings = useMemo(() => {
        if (!currentToken) {
            return null;
        }

        const { exp, iat } = extractFromJWT(currentToken);
        return {
            expirationDate: dayjs(exp * 1000),
            tokenLifetimeMs: (exp - iat) * 1000,
        };
    }, [currentToken]);

    // warning toast before session expiry
    useEffect(() => {
        if (!tokenTimings) {
            return () => undefined;
        }

        const { expirationDate, tokenLifetimeMs } = tokenTimings;
        // clamp the warning lead time to at most half the token lifetime
        // so short-lived tokens (e.g. during testing) still get a meaningful warning
        const effectiveWarningLeadMs = Math.min(WARNING_BEFORE_EXPIRY_MS, Math.floor(tokenLifetimeMs / 2));
        const msUntilWarning = expirationDate
            .subtract(effectiveWarningLeadMs, 'millisecond')
            .diff(dayjs(), 'millisecond');
        const msUntilExpiry = expirationDate.diff(dayjs(), 'millisecond');

        let warningTimeoutId: NodeJS.Timeout | null = null;
        let warningVisible = false;

        const showWarning = () => {
            // only show warning if user has been inactive
            if (!wasActiveWithin(tokenLifetimeMs - effectiveWarningLeadMs)) {
                const minutesRemaining = Math.max(Math.round(msUntilExpiry / 60_000), 1);
                showWarningNotification({
                    id: INACTIVITY_WARNING_ID,
                    message: t('sessionTimeout.warningTitle'),
                    description: t('sessionTimeout.warningDescription', { count: minutesRemaining }),
                    duration: Math.max(msUntilExpiry, 0),
                });

                warningVisible = true;

                // piggyback on the existing activity tracker listeners
                // to auto-dismiss the warning toast on the next interaction
                setOnActivity(() => {
                    removeNotification(INACTIVITY_WARNING_ID);
                    warningVisible = false;
                    setOnActivity(null);
                });
            }
        };

        // only schedule a warning while the token is still valid
        if (msUntilExpiry > 0) {
            if (msUntilWarning > 0) {
                // normal case: schedule warning for later
                warningTimeoutId = setTimeout(() => {
                    warningTimeoutId = null;
                    showWarning();
                }, msUntilWarning);
            } else {
                // loaded within the warning window — show immediately
                showWarning();
            }
        }

        return () => {
            if (warningTimeoutId) {
                clearTimeout(warningTimeoutId);
            }
            if (warningVisible) {
                setOnActivity(null);
            }
        };
    }, [tokenTimings, wasActiveWithin, setOnActivity, t]);

    // token refresh based on user activity
    useEffect(() => {
        if (!tokenTimings) {
            return () => undefined;
        }

        const { expirationDate, tokenLifetimeMs } = tokenTimings;
        const msUntilRefresh = expirationDate
            .subtract(REFRESH_BEFORE_EXPIRY_MS, 'millisecond')
            .diff(dayjs(), 'millisecond');

        let timeoutId: NodeJS.Timeout | null = null;
        let mounted = true;

        const refreshToken = async () => {
            if (expirationDate.isBefore(dayjs())) {
                // token already expired
                timeoutId = null;
                const wasActive = wasActiveWithin(tokenLifetimeMs);
                logout(wasActive ? undefined : 'inactivity');
                return;
            }

            // check if user was active during this session window
            if (wasActiveWithin(tokenLifetimeMs - REFRESH_BEFORE_EXPIRY_MS)) {
                // user was active — extend the session
                timeoutId = null;
                removeNotification(INACTIVITY_WARNING_ID);

                try {
                    const response = await apolloClient.mutate({
                        mutation: ExtendSessionMutation,
                    });

                    updateToken(response?.data?.token || null);
                } catch (error) {
                    console.info('Failed to renew the session');
                    console.error(error);
                    if (mounted) {
                        timeoutId = setTimeout(refreshToken, 500);
                    }
                }
            } else {
                // user was NOT active — schedule a final check
                const msUntilExpiry = Math.max(expirationDate.diff(dayjs(), 'millisecond'), 0);
                const msUntilFinalCheck = Math.max(msUntilExpiry - FINAL_CHECK_SAFETY_MARGIN_MS, 0);
                timeoutId = setTimeout(() => {
                    timeoutId = null;
                    if (!mounted) {
                        return;
                    }

                    if (wasActiveWithin(msUntilFinalCheck || REFRESH_BEFORE_EXPIRY_MS)) {
                        // user became active since last check — attempt refresh
                        refreshToken();
                    } else {
                        logout('inactivity');
                    }
                }, msUntilFinalCheck);
            }
        };

        timeoutId = setTimeout(refreshToken, Math.max(msUntilRefresh, 0));

        return () => {
            mounted = false;

            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [tokenTimings, logout, apolloClient, wasActiveWithin]);

    return { logout };
};

export default useSessionLifecycle;
